import type { ConsiderationStatus } from '@/api/vocabularies';

/**
 * How much emphasis *Schedule an interview* carries at each status — `DESIGN.md` §6.5, *The block's
 * composition*.
 *
 * ⚠️ **This changes the emphasis and never the offer, and the distinction is the whole reason the
 * module is this small.** Scheduling is offered at **all five** statuses and nothing here can withhold
 * it: §6.5's *Schedule an interview* section says so outright, ADR 0010's table is total, and *"you
 * cannot interview a rejected candidate"* invented on the client would be the first legal-transition
 * rule in the product. A caller reads a **variant** from this table, never a yes or a no.
 *
 * A filled button on a closed run would be the product proposing that somebody interview a candidate
 * they have already hired — a nudge, and §1 reserves emphasis for meaning. An outlined one still offers
 * the act to anybody who wants it, which is the honest rendering of *"available but not the obvious
 * next thing"*.
 *
 * ⚠️ **It is a function of the status alone.** Never of `average_score`, never of
 * `latest_recommendation`, never of an interview row — ADR 0010 rejected deriving an outcome from
 * feedback outright, and a signature that cannot see those values is the cheapest guarantee of it.
 *
 * The `Record<ConsiderationStatus, …>` is load-bearing for the same reason `STATUS_AFFORDANCE`'s is: a
 * status added to the backend's enum stops `tsc` **here**, at the one place that has to decide what
 * emphasis it carries, rather than defaulting to one silently.
 */
const SCHEDULE_EMPHASIS: Record<ConsiderationStatus, 'primary' | 'secondary'> = {
  /** Nobody has spoken to them yet, so arranging the first conversation is the obvious next act. */
  NEW: 'primary',
  /** Mid-process: the next conversation is the ordinary next thing. */
  INTERVIEWING: 'primary',
  /** An offer is outstanding, and a further conversation is still an ordinary part of closing it. */
  OFFER: 'primary',
  /** The run is closed. Still offered — a conversation after a hire happens — but not proposed. */
  HIRED: 'secondary',
  /** Closed the other way. A second round is ordinary (ADR 0010); it is not what this card suggests. */
  REJECTED: 'secondary',
} as const;

/** The variant *Schedule an interview* renders at, for a run at this status. */
export const scheduleEmphasisFor = (status: ConsiderationStatus): 'primary' | 'secondary' =>
  SCHEDULE_EMPHASIS[status];
